/**
 * reCAPTCHA v3 — carga lazy del script y ejecución por acción.
 * Requiere VUE_APP_RECAPTCHA_SITE_KEY en el frontend.
 */

const SITE_KEY = process.env.VUE_APP_RECAPTCHA_SITE_KEY || '';

let loadPromise = null;

function loadRecaptchaScript() {
  if (!SITE_KEY) {
    return Promise.resolve(false);
  }

  if (window.grecaptcha?.execute) {
    return Promise.resolve(true);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existing) {
      if (window.grecaptcha?.execute) {
        resolve(true);
        return;
      }
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => reject(new Error('Error cargando reCAPTCHA')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Error cargando reCAPTCHA'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

export function isRecaptchaEnabled() {
  return SITE_KEY.length > 0;
}

/**
 * Obtiene un token reCAPTCHA v3 para la acción indicada.
 * @param {string} action - ej. 'signup', 'google_signup', 'password_reset'
 * @returns {Promise<string|null>} token o null si reCAPTCHA no está configurado
 */
export async function executeRecaptcha(action) {
  if (!SITE_KEY) {
    return null;
  }

  await loadRecaptchaScript();

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha.execute(SITE_KEY, { action });
        resolve(token);
      } catch (err) {
        reject(err);
      }
    });
  });
}

/**
 * Verifica el token con el backend antes de operaciones sensibles (signup).
 */
export async function verifyRecaptchaWithBackend(action) {
  const token = await executeRecaptcha(action);

  if (!token) {
    if (!isRecaptchaEnabled()) {
      return null;
    }
    throw new Error('recaptcha_failed');
  }

  const response = await fetch('/api/users/verify-recaptcha', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, action }),
  });

  const data = await response.json().catch(() => ({}));

  if (response.status === 429) {
    throw new Error(data.error || 'rate_limit');
  }

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'recaptcha_failed');
  }

  return token;
}

/** Mensaje de error unificado para mostrar al usuario */
export function getRecaptchaErrorMessage(error) {
  if (error?.message === 'rate_limit' || (typeof error === 'string' && error.includes('Demasiados'))) {
    return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
  }
  return 'No pudimos verificar la solicitud. Recarga la página e inténtalo de nuevo.';
}

/**
 * Obtiene un token fresco para enviar en el body de endpoints protegidos (recaptchaToken).
 * Cada llamada genera un token nuevo (siteverify solo acepta cada token una vez).
 */
export async function getRecaptchaTokenForApi(action) {
  const token = await executeRecaptcha(action);

  if (!token) {
    if (!isRecaptchaEnabled()) {
      return null;
    }
    throw new Error('recaptcha_failed');
  }

  return token;
}
